import { useState } from 'react';
import curriculum from '../assets/maths_curriculum.json';
import './ExploreCurriculumPage.css';
import SEO from '../components/SEO';

const ExploreCurriculumPage = () => {
  const [selectedYear, setSelectedYear] = useState('year_3');
  const [expandedStrand, setExpandedStrand] = useState('number_and_place_value');

  // Example completed tasks for demonstration
  const completedTaskIds = [
    'count_to_and_across_100',
    'understand_tens_and_ones',
    'compare_numbers_greater_less',
    'compare_quantities_more_fewer_same',
    'round_to_nearest_10',
    'recognise_numerals_to_10',
    'work_with_numbers_to_1000',
    'identify_right_angles',
    'share_quantities_equally',
  ];

  const strands = Object.keys(curriculum).map((key) => ({
    id: key,
    name: key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase()),
  }));

  const allYears = ['reception', 'year_1', 'year_2', 'year_3', 'year_4', 'year_5', 'year_6'];

  const getYearLabel = (year) => {
    const labels = {
      reception: 'EYFS',
      year_1: 'Y1',
      year_2: 'Y2',
      year_3: 'Y3',
      year_4: 'Y4',
      year_5: 'Y5',
      year_6: 'Y6',
    };
    return labels[year];
  };

  const toggleStrand = (strandId) => {
    setExpandedStrand(expandedStrand === strandId ? null : strandId);
  };

  const getTasksForYearAndStrand = (year, strandId) => {
    const strandData = curriculum[strandId];
    return strandData[year] || [];
  };

  return (
    <div className="curriculum-page">
      <SEO
        title="Explore Curriculum | UK Maths Objectives for Ages 5-8"
        description="Browse Sprout Academy's interactive maths curriculum map with UK National Curriculum objectives by strand and year group."
        path="/curriculum"
      />
      <div className="curriculum-page__header">
        <div className="curriculum-page__top-bar">
          <div className="curriculum-page__header-content">
            <h1>Explore the Curriculum</h1>
          </div>
        </div>

        <div className="curriculum-page__year-bar">
          <p className="curriculum-page__year-label">Select year group:</p>
          <div className="curriculum-page__year-buttons">
            {allYears.map((year) => (
              <button
                key={year}
                className={`curriculum-page__year-btn ${
                  selectedYear === year ? 'curriculum-page__year-btn--active' : ''
                }`}
                onClick={() => setSelectedYear(year)}
              >
                {getYearLabel(year)}
              </button>
            ))}
          </div>
        </div>
      </div>

      <main className="curriculum-page__main">
        <div className="curriculum-page__intro">
          <p>
            Every quest in Sprout Academy aligns with specific National Curriculum objectives. Click any strand to see what your child will learn.
          </p>
        </div>

        <div className="curriculum-page__strands">
          {strands.map((strand) => {
            const tasks = getTasksForYearAndStrand(selectedYear, strand.id);
            const completed = tasks.filter((task) => completedTaskIds.includes(task.id)).length;
            const percent = tasks.length > 0 ? (completed / tasks.length) * 100 : 0;
            const isExpanded = expandedStrand === strand.id;

            return (
              <div
                key={strand.id}
                className={`curriculum-strand ${isExpanded ? 'curriculum-strand--expanded' : ''}`}
              >
                <button
                  className="curriculum-strand__header"
                  onClick={() => toggleStrand(strand.id)}
                >
                  <div className="curriculum-strand__title-group">
                    <span className="curriculum-strand__icon">
                      {isExpanded ? '▼' : '▶'}
                    </span>
                    <h2 className="curriculum-strand__title">{strand.name}</h2>
                  </div>
                  <div className="curriculum-strand__progress">
                    <div className="curriculum-strand__progress-bar">
                      <div
                        className="curriculum-strand__progress-fill"
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                    <span className="curriculum-strand__progress-text">
                      {completed}/{tasks.length}
                    </span>
                  </div>
                </button>

                {isExpanded && (
                  <div className="curriculum-strand__content">
                    {tasks.length > 0 ? (
                      <ul className="curriculum-strand__tasks">
                        {tasks.map((task) => {
                          const isCompleted = completedTaskIds.includes(task.id);
                          return (
                            <li
                              key={task.id}
                              className={`curriculum-task ${
                                isCompleted ? 'curriculum-task--completed' : ''
                              }`}
                            >
                              <span className="curriculum-task__checkbox">
                                {isCompleted && '✓'}
                              </span>
                              <span className="curriculum-task__text">
                                {task.description}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <p className="curriculum-strand__empty">
                        Coming soon for this year group.
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="curriculum-page__cta">
          <div className="curriculum-page__cta-card">
            <h3>Ready to try it?</h3>
            <p>
              Weekly progress emails show exactly which curriculum objectives your child has mastered. No guessing. Just clear, honest progress.
            </p>
            <button className="btn btn--primary">Join the Founding Families list</button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ExploreCurriculumPage;
